import type { RouteObject } from "react-router-dom";

import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyMyAppLayout,
  MyAppCreate,
  MyAppLibrary,
  MyAppAppStore,
  MyAppView,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_MY_APP_ID_KEY = "id";

// Where the create flow was entered from. The builder is reachable both from the
// My Apps library and from the Studio hub's "Apps" card, and Back/✕ must return
// to whichever one the user came from. A query param (not nav state) so the exit
// survives a refresh or a shared link.
export const COMPANY_MY_APP_FROM_KEY = "from";
export const COMPANY_MY_APP_FROM_STUDIO = "studio";

export enum COMPANY_MY_APP_PATH_SEGMENTS {
  // URL slug is plural ("my-apps"); permission section is "my apps".
  ROOT = "my-apps",
  CREATE = "create",
  APP_STORE = "app-store",
  APP_ID = `:${COMPANY_MY_APP_ID_KEY}`,
}

const S = COMPANY_MY_APP_PATH_SEGMENTS;

// ─── Route Definition (inside Layout) ────────────────────────────────────────
// Every My App screen keeps the app chrome (sidebar + topbar): the library
// grid, the describe/generate flow, and the app runtime all render in the
// content area. `:id` is the saved app opened from the library (runs on open).
export const companyMyAppRoutes: RouteObject = {
  path: S.ROOT,
  handle: { breadcrumb: "My Apps" },
  HydrateFallback: Spinner,
  children: [
    // The library sits under the tabbed layout (white panel + top tabs). Create
    // and the app runtime are standalone sub-flows — NOT children of the layout,
    // so they never render under the tab bar. Static "create" outranks ":id".
    {
      Component: CompanyMyAppLayout,
      handle: { flushContent: true },
      children: [
        { index: true, Component: MyAppLibrary },
        {
          path: S.APP_STORE,
          handle: { breadcrumb: "App Store" },
          Component: MyAppAppStore,
        },
      ],
    },
    {
      path: S.CREATE,
      handle: { breadcrumb: "Create app" },
      Component: MyAppCreate,
    },
    {
      path: S.APP_ID,
      handle: { breadcrumb: "App" },
      Component: MyAppView,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_MY_APP_PATHS = Object.freeze({
  /** The app library grid (module landing). */
  ROOT: withCompanyPrefix(addSlashes(S.ROOT)),
  /** Describe + generate a new app. */
  CREATE: withCompanyPrefix(addSlashes(S.ROOT, S.CREATE)),
  /** Same builder, entered from the Studio hub — Back/✕ return to Studio. */
  CREATE_FROM_STUDIO: `${withCompanyPrefix(addSlashes(S.ROOT, S.CREATE))}?${COMPANY_MY_APP_FROM_KEY}=${COMPANY_MY_APP_FROM_STUDIO}`,
  /** The app store catalog tab. */
  APP_STORE: withCompanyPrefix(addSlashes(S.ROOT, S.APP_STORE)),
  /** Open a saved app by id (runs on open). */
  APP: (id: string | number) =>
    withCompanyPrefix(addSlashes(S.ROOT, String(id))),
});
